import dayjs from '@/components/NewUI/utils/dayjs';

import { BOOKING_RULES } from './config';

import type { Dayjs } from 'dayjs';

import type { ISlot } from './types';

// Pure booking-window and formatting helpers. Every function takes "now"
// explicitly (the server's clock, from the page props), so tests never read
// the real one.

const DAY_FORMAT = 'YYYY-MM-DD';

interface IBookingLimits {
  earliest: Dayjs;
  latest: Dayjs;
  canShowLater: boolean;
}

// Earliest is now + 1 hour. Latest is now + 7 days or, once the candidate asks
// for later dates, the link's latest bookable time (14 days after the email),
// never further.
export const bookingLimits = (
  nowIso: string,
  latestAtIso: string,
  showLaterDates: boolean
): IBookingLimits => {
  const earliest = dayjs(nowIso).add(BOOKING_RULES.leadMinutes, 'minute');
  const extendedLatest = dayjs(latestAtIso);
  const sevenDays = dayjs(nowIso).add(BOOKING_RULES.defaultRangeDays, 'day');
  const defaultLatest = sevenDays.isAfter(extendedLatest) ? extendedLatest : sevenDays;
  const canShowLater = extendedLatest.isAfter(defaultLatest);
  return {
    earliest,
    latest: showLaterDates && canShowLater ? extendedLatest : defaultLatest,
    canShowLater
  };
};

const pad = (n: number) => String(n).padStart(2, '0');

// Bookable slots grouped by local day in `timeZone`: weekdays only, every 15
// minutes from 9:30am to 7:00pm inclusive, at least an hour from now.
export const buildSlots = ({
  nowIso,
  latestAtIso,
  timeZone,
  showLaterDates
}: {
  nowIso: string;
  latestAtIso: string;
  timeZone: string;
  showLaterDates: boolean;
}): Map<string, ISlot[]> => {
  const { earliest, latest } = bookingLimits(nowIso, latestAtIso, showLaterDates);
  const lastDayKey = latest.tz(timeZone).format(DAY_FORMAT);
  const slotsByDay = new Map<string, ISlot[]>();

  // Walk calendar days as plain dates so DST changes never skip or repeat a day.
  let dayKey = earliest.tz(timeZone).format(DAY_FORMAT);
  while (dayKey <= lastDayKey) {
    const weekday = dayjs(dayKey, DAY_FORMAT).day();
    if (weekday !== 0 && weekday !== 6) {
      const daySlots: ISlot[] = [];
      for (
        let minutes = BOOKING_RULES.windowStartMinutes;
        minutes <= BOOKING_RULES.windowEndMinutes;
        minutes += BOOKING_RULES.stepMinutes
      ) {
        const local = `${dayKey} ${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
        const slot = dayjs.tz(local, 'YYYY-MM-DD HH:mm', timeZone);
        if (!slot.isBefore(earliest) && !slot.isAfter(latest)) {
          daySlots.push({ iso: slot.toISOString(), dayKey, minutes });
        }
      }
      if (daySlots.length > 0) slotsByDay.set(dayKey, daySlots);
    }
    dayKey = dayjs(dayKey, DAY_FORMAT).add(1, 'day').format(DAY_FORMAT);
  }

  return slotsByDay;
};

export const SLOT_PERIODS = [
  { label: 'Morning', from: 0, to: 12 * 60 },
  { label: 'Afternoon', from: 12 * 60, to: 17 * 60 },
  { label: 'Evening', from: 17 * 60, to: 24 * 60 }
] as const;

export const formatTime = (iso: string, timeZone: string): string =>
  dayjs(iso).tz(timeZone).format('h:mma');

export const formatDay = (iso: string, timeZone: string): string =>
  dayjs(iso).tz(timeZone).format('ddd D MMM');

export const formatLongDay = (iso: string, timeZone: string): string =>
  dayjs(iso).tz(timeZone).format('dddd D MMMM YYYY');

export const zoneAbbreviation = (iso: string, timeZone: string): string =>
  dayjs(iso).tz(timeZone).format('z');

// "Wed 28 Oct, 2:15pm AEDT": the format the badges and summaries use.
export const formatSlot = (iso: string, timeZone: string): string =>
  `${formatDay(iso, timeZone)}, ${formatTime(iso, timeZone)} ${zoneAbbreviation(iso, timeZone)}`;

export const dayKeyLabel = (dayKey: string): string =>
  dayjs(dayKey, DAY_FORMAT).format('dddd D MMMM');

export const dayKeyOf = (iso: string, timeZone: string): string =>
  dayjs(iso).tz(timeZone).format(DAY_FORMAT);

// "Sydney" from "Australia/Sydney", "Port of Spain" from "America/Port_of_Spain".
export const zoneCity = (timeZone: string): string =>
  (timeZone.split('/').pop() ?? timeZone).replace(/_/g, ' ');

// "AEDT, GMT+11:00" at a given instant (offsets move with daylight saving).
// Zones without a named abbreviation show the offset once.
export const offsetLabel = (timeZone: string, atIso: string): string => {
  const at = dayjs(atIso).tz(timeZone);
  const offset = `GMT${at.format('Z')}`;
  const abbreviation = at.format('z');
  return /^[A-Z]{2,5}$/.test(abbreviation) && abbreviation !== 'GMT'
    ? `${abbreviation}, ${offset}`
    : offset;
};

// "Sydney (AEDT, GMT+11:00)": readable time zone option.
export const timeZoneLabel = (timeZone: string, atIso: string): string =>
  `${zoneCity(timeZone)} (${offsetLabel(timeZone, atIso)})`;

export const normalizePhone = (phone: string): string => phone.replace(/[\s().-]/g, '');

// Australian national numbers become +61 so every later step sees one shape.
const toInternational = (phone: string): string => {
  const value = normalizePhone(phone);
  return /^0[2-478]\d{8}$/.test(value) ? `+61${value.slice(1)}` : value;
};

// Accepts an Australian mobile or landline (0412 345 678, 02 9876 5432) or any
// international number with a leading + and 8 to 15 digits. The server stays
// authoritative; this only catches typos before the candidate confirms.
export const phoneError = (phone: string): string | null => {
  const value = normalizePhone(phone);
  if (!value) return 'Enter the number Rosie should call.';
  if (/^0[2-478]\d{8}$/.test(value)) return null;
  if (/^\+\d{8,15}$/.test(value)) return null;
  return 'Enter a valid phone number, like 0412 345 678.';
};

// A valid number the candidate has just typed, written out in full on the
// page they're typing it on: "+61 498 765 321".
export const displayPhone = (phone: string): string => {
  const value = toInternational(phone);
  if (/^\+614\d{8}$/.test(value)) {
    return `+61 ${value.slice(3, 6)} ${value.slice(6, 9)} ${value.slice(9)}`;
  }
  if (/^\+61[2378]\d{8}$/.test(value)) {
    return `+61 ${value[3]} ${value.slice(4, 8)} ${value.slice(8)}`;
  }
  return phone.trim();
};

// Minimal iCalendar file for "Add to calendar" on the confirmation page. The
// confirmation email carries the same invite (AiVoiceInterview::BookingCalendar).
export const calendarHref = ({
  slotIso,
  companyName,
  jobTitle,
  maskedPhone
}: {
  slotIso: string;
  companyName: string;
  jobTitle: string;
  maskedPhone: string;
}): string => {
  const stamp = (iso: string) => dayjs(iso).utc().format('YYYYMMDD[T]HHmmss[Z]');
  const end = dayjs(slotIso)
    .add(BOOKING_RULES.callMinutes + 5, 'minute')
    .toISOString();
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Scout Talent//AI phone interview//EN',
    'BEGIN:VEVENT',
    `UID:${stamp(slotIso)}-ai-phone-interview@scouttalent`,
    `DTSTAMP:${stamp(slotIso)}`,
    `DTSTART:${stamp(slotIso)}`,
    `DTEND:${stamp(end)}`,
    `SUMMARY:Phone interview with Rosie (${companyName})`,
    `DESCRIPTION:Rosie\\, ${companyName}'s AI interviewer\\, will call ${maskedPhone} about the ${jobTitle} role.`,
    'END:VEVENT',
    'END:VCALENDAR'
  ];
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join('\r\n'))}`;
};
